/**
 * Relationship Detail Panel
 * Comprehensive view of relationship assessment with human-in-the-loop actions
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  ButtonGroup,
  IconButton,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Slider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  EmojiObjects as InsightIcon,
  Assignment as ActionIcon,
} from '@mui/icons-material';
import {
  getFaanaHealthColor,
  getDimensionColor,
  getGrowthIndicator,
  formatDimensionName,
  getActionableInsight,
  getInterventionUrgency,
} from '../utils/faanaScoring';

// eslint-disable-next-line max-lines-per-function
function RelationshipDetailPanel({
  relationship,
  onClose,
  onConfirm,
  onReject,
  onUpdate
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [noteDialog, setNoteDialog] = useState(false);
  const [note, setNote] = useState('');
  const [adjustmentDialog, setAdjustmentDialog] = useState(false);
  const [adjustments, setAdjustments] = useState({
    confidence: relationship?.confidence || 'MEDIUM',
    healthOverride: relationship?.overallHealth || 50,
  });

  if (!relationship) return null;

  const dimensions = relationship.dimensions || {};
  const interventionUrgency = getInterventionUrgency(
    relationship.overallHealth,
    relationship.trajectoryPrediction
  );
  const growthIndicator = getGrowthIndicator(
    relationship.growthIndicator || 'steady'
  );

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
  };

  const handleReject = () => {
    if (onReject) onReject();
  };

  const handleAddNote = () => {
    setNoteDialog(true);
  };

  const handleSaveNote = () => {
    // Implementation would save note via API
    setNoteDialog(false);
    setNote('');
  };

  const handleAdjust = () => {
    setAdjustmentDialog(true);
  };

  const handleSaveAdjustments = () => {
    if (onUpdate) {
      onUpdate({
        confidence: adjustments.confidence,
        overallHealth: adjustments.healthOverride,
      });
    }
    setAdjustmentDialog(false);
  };

  const renderDimensionCard = (dimension, data) => {
    if (!data) return null;

    const insight = getActionableInsight(dimension, data.score);

    return (
      <Card key={dimension} sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">
              {formatDimensionName(dimension)}
            </Typography>
            <Chip
              label={`${data.score}/10`}
              sx={{
                backgroundColor: getDimensionColor(data.score),
                color: 'white',
                fontWeight: 'bold',
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={(data.score / 10) * 100}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getDimensionColor(data.score),
                },
              }}
            />
          </Box>

          {/* Dimension-specific details */}
          {dimension === 'trust' && data.indicators && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Trust Indicators
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {data.indicators.map((indicator, idx) => (
                  <Chip key={idx} label={indicator} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}

          {dimension === 'communication' && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Chip
                label={data.effectiveness || 'Unknown'}
                size="small"
                color={
                  data.effectiveness === 'highly_effective' ? 'success' :
                  data.effectiveness === 'effective' ? 'primary' :
                  data.effectiveness === 'adequate' ? 'warning' : 'error'
                }
              />
              {data.patterns && data.patterns.length > 0 && (
                <Chip
                  label={`${data.patterns.length} patterns`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          )}

          {dimension === 'psychologicalSafety' && (
            <Box sx={{ mb: 2 }}>
              <Chip
                label={data.level || 'Unknown'}
                size="small"
                color={
                  data.level === 'high' ? 'success' :
                  data.level === 'moderate' ? 'primary' :
                  data.level === 'low' ? 'warning' : 'error'
                }
              />
            </Box>
          )}

          {/* Actionable insight */}
          <Alert
            severity={data.score < 5 ? 'warning' : 'info'}
            icon={<InsightIcon />}
            sx={{ mb: 1 }}
          >
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              {insight.insight}
            </Typography>
            <Typography variant="body2">
              <strong>Recommended Action:</strong> {insight.action}
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  };

  // eslint-disable-next-line max-lines-per-function
  const renderOverviewTab = () => (
    <Box sx={{ p: 2 }}>
      {/* Health Score */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5">
              Overall Health Score
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: getFaanaHealthColor(relationship.overallHealth),
                fontWeight: 'bold'
              }}
            >
              {relationship.overallHealth}
            </Typography>
          </Box>

          <LinearProgress
            variant="determinate"
            value={relationship.overallHealth}
            sx={{
              height: 12,
              borderRadius: 6,
              mb: 2,
              '& .MuiLinearProgress-bar': {
                backgroundColor: getFaanaHealthColor(relationship.overallHealth),
              },
            }}
          />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              label={relationship.healthCategory || 'Unknown'}
              sx={{
                backgroundColor: getFaanaHealthColor(relationship.overallHealth),
                color: 'white',
              }}
            />
            <Chip
              label={relationship.confidence}
              variant="outlined"
            />
            {relationship.trajectoryPrediction && (
              <Chip
                icon={
                  relationship.trajectoryPrediction === 'strengthening' ? <TrendingUpIcon /> :
                  relationship.trajectoryPrediction === 'weakening' ? <TrendingDownIcon /> :
                  <TrendingFlatIcon />
                }
                label={relationship.trajectoryPrediction}
                color={
                  relationship.trajectoryPrediction === 'strengthening' ? 'success' :
                  relationship.trajectoryPrediction === 'weakening' ? 'error' : 'default'
                }
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Growth Indicator */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">Growth Trajectory</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '1.5em' }}>{growthIndicator.icon}</span>
              <Typography
                variant="body1"
                sx={{ color: growthIndicator.color, fontWeight: 'bold' }}
              >
                {growthIndicator.label}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Intervention Urgency */}
      <Alert
        severity={
          interventionUrgency.level === 'critical' ? 'error' :
          interventionUrgency.level === 'high' ? 'warning' :
          interventionUrgency.level === 'medium' ? 'info' : 'success'
        }
        icon={<WarningIcon />}
        sx={{ mb: 3 }}
      >
        <Typography variant="body1" fontWeight="bold">
          {interventionUrgency.message}
        </Typography>
      </Alert>

      {/* Evidence Summary */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Evidence ({relationship.evidence?.length || 0} items)
          </Typography>
          {relationship.evidence?.slice(0, 3).map((evidence, idx) => (
            <Box key={idx} sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {evidence.type}: {evidence.description}
              </Typography>
            </Box>
          ))}
          {relationship.evidence?.length > 3 && (
            <Typography variant="body2" color="primary" sx={{ cursor: 'pointer' }}>
              View {relationship.evidence.length - 3} more...
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  const renderDimensionsTab = () => (
    <Box sx={{ p: 2 }}>
      {Object.entries(dimensions).map(([dimension, data]) =>
        renderDimensionCard(dimension, data)
      )}
    </Box>
  );

  const renderActionsTab = () => (
    <Box sx={{ p: 2 }}>
      {/* Quick Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            {!relationship.isConfirmed && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckIcon />}
                  onClick={handleConfirm}
                >
                  Confirm
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={handleReject}
                >
                  Reject
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleAdjust}
            >
              Adjust
            </Button>
          </Box>
          <Button
            variant="text"
            startIcon={<ActionIcon />}
            onClick={handleAddNote}
            fullWidth
          >
            Add Note
          </Button>
        </CardContent>
      </Card>

      {/* Recommendations */}
      {relationship.insights?.recommendations && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recommended Actions
            </Typography>
            <List>
              {relationship.insights.recommendations.map((rec, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <Chip
                      label={rec.priority}
                      size="small"
                      color={
                        rec.priority === 'high' ? 'error' :
                        rec.priority === 'medium' ? 'warning' : 'success'
                      }
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={rec.action}
                    secondary={`Expected impact: ${rec.expectedImpact} | Timeline: ${rec.timeframe}`}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
    </Box>
  );

  return (
    <Paper
      sx={{
        position: 'fixed',
        right: 16,
        top: 50,
        bottom: 16,
        width: 400,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 100px)',
      }}
      elevation={8}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" noWrap>
            {relationship.relationshipType}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {relationship.sourceEntity?.name} → {relationship.targetEntity?.name}
        </Typography>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, newTab) => setActiveTab(newTab)}
        variant="fullWidth"
      >
        <Tab label="Overview" />
        <Tab label="Dimensions" />
        <Tab label="Actions" />
      </Tabs>

      {/* Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {activeTab === 0 && renderOverviewTab()}
        {activeTab === 1 && renderDimensionsTab()}
        {activeTab === 2 && renderActionsTab()}
      </Box>

      {/* Add Note Dialog */}
      <Dialog open={noteDialog} onClose={() => setNoteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Note</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={4}
            fullWidth
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveNote} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Adjustment Dialog */}
      <Dialog open={adjustmentDialog} onClose={() => setAdjustmentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adjust Relationship</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography gutterBottom>Confidence Level</Typography>
            <ButtonGroup fullWidth>
              {['LOW', 'MEDIUM', 'HIGH'].map(level => (
                <Button
                  key={level}
                  variant={adjustments.confidence === level ? 'contained' : 'outlined'}
                  onClick={() => setAdjustments({ ...adjustments, confidence: level })}
                >
                  {level}
                </Button>
              ))}
            </ButtonGroup>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography gutterBottom>Health Override</Typography>
            <Slider
              value={adjustments.healthOverride}
              onChange={(e, value) => setAdjustments({ ...adjustments, healthOverride: value })}
              min={0}
              max={100}
              marks={[
                { value: 0, label: '0' },
                { value: 40, label: '40' },
                { value: 60, label: '60' },
                { value: 80, label: '80' },
                { value: 100, label: '100' },
              ]}
              valueLabelDisplay="auto"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustmentDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveAdjustments} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default RelationshipDetailPanel;